'use client'

import { Calendar, Layout, Video } from 'react-feather'
import FAQs from '../../sections/FAQs'
import Hero from '../../sections/Hero'
import { useTranslations, Session } from '@/lib/next-shim'
import { scrollToSection } from '@/utils/scrollToSection'

type DefaultLandingPageProps = {
  session?: Session
}

const FEATURES = [
  { key: 'booking', icon: Calendar },
  { key: 'consultation', icon: Video },
  { key: 'dashboard', icon: Layout },
] as const

export default function DefaultLandingPage({ session }: DefaultLandingPageProps) {
  const t = useTranslations()

  return (
    <main className="overflow-x-hidden">
      <Hero session={session ?? undefined} />

      {/* Features Section */}
      <section id="features" className="scroll-mt-24 bg-gray-50 py-12 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{t('marketing.sections.PrimaryFeatures.title')}</h2>
            <p className="mt-2 text-lg leading-8 text-gray-600">{t('marketing.sections.PrimaryFeatures.subtitle')}</p>
          </div>
          <div className="mx-auto mt-12 grid max-w-2xl grid-cols-1 gap-8 sm:mt-16 lg:max-w-none lg:grid-cols-3">
            {FEATURES.map(({ key, icon: Icon }) => (
              <div key={key} className="flex flex-col items-start rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <h3 className="mt-4 text-lg font-semibold leading-8 text-gray-900">{t(`marketing.sections.PrimaryFeatures.feature.${key}.name`)}</h3>
                <p className="mt-2 text-base leading-7 text-gray-600">{t(`marketing.sections.PrimaryFeatures.feature.${key}.description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="bg-white py-12 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{t('page.Home.howItWorks.title')}</h2>
            <p className="mt-2 text-lg leading-8 text-gray-600">{t('page.Home.howItWorks.subtitle')}</p>
          </div>
          <div className="mx-auto mt-12 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-10 sm:mt-16 sm:grid-cols-2 sm:gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            {/* Step 1 */}
            <div className="flex flex-col items-start">
              <div className="rounded-md bg-primary/10 p-2 ring-1 ring-primary/20">
                <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-white font-bold">1</span>
                </div>
              </div>
              <h3 className="mt-4 text-lg font-semibold leading-8 text-gray-900">{t('page.Home.howItWorks.step1.title')}</h3>
              <p className="mt-2 text-base leading-7 text-gray-600">{t('page.Home.howItWorks.step1.description')}</p>
            </div>

            {/* Step 2 */}
            <div className="flex flex-col items-start">
              <div className="rounded-md bg-primary/10 p-2 ring-1 ring-primary/20">
                <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-white font-bold">2</span>
                </div>
              </div>
              <h3 className="mt-4 text-lg font-semibold leading-8 text-gray-900">{t('page.Home.howItWorks.step2.title')}</h3>
              <p className="mt-2 text-base leading-7 text-gray-600">{t('page.Home.howItWorks.step2.description')}</p>
            </div>

            {/* Step 3 */}
            <div className="flex flex-col items-start">
              <div className="rounded-md bg-primary/10 p-2 ring-1 ring-primary/20">
                <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-white font-bold">3</span>
                </div>
              </div>
              <h3 className="mt-4 text-lg font-semibold leading-8 text-gray-900">{t('page.Home.howItWorks.step3.title')}</h3>
              <p className="mt-2 text-base leading-7 text-gray-600">{t('page.Home.howItWorks.step3.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-primary py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">{t('page.Home.callToAction.title')}</h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-gray-300">{t('page.Home.callToAction.subtitle')}</p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <a href="/register" className="rounded-md bg-white px-6 py-3 text-lg font-semibold text-primary shadow-sm hover:bg-gray-100">
                {t('button.signUp.label')}
              </a>
              <a
                href="#meeting-links"
                onClick={(e) => {
                  e.preventDefault()
                  scrollToSection('meeting-links')
                }}
                className="text-lg font-semibold leading-6 text-white hover:text-gray-300"
              >
                {t('page.Home.callToAction.browseButton')} <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>
      <FAQs />
    </main>
  )
}
